import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import KakaoMap from '../components/KakaoMap';
import BuildingClassificationEditModal from '../components/BuildingClassificationEditModal';
import BuildingInfoEditModal from '../components/BuildingInfoEditModal';
import { formatDongDisplayName } from '../utils/dong';

interface Building {
  id: number;
  dongNumber: number;
  dongName?: string | null;
  shapeType: string;
  coordinates: any;
  maxFloor: number;
  unitPattern: any;
  buildingType?: string;
  accessibility?: string;
  accessNotes?: string;
  entranceCount?: number;
  hasElevator?: boolean;
  hasIntercom?: boolean | null;
  complex: {
    id: number;
    name: string;
    code: number;
  };
  _count: {
    units: number;
  };
}

interface Unit {
  id: number;
  floor: number;
  suffix: number;
  code: string;
  buildingId: number;
}

interface Complex {
  id: number;
  code: number;
  name: string;
  alias?: string;
  address: string;
  complexNo: number;
  groupName?: string | null;
  buildings: Building[];
}

interface ComplexResponse {
  message: string;
  data: Complex[];
  count: number;
}

interface BuildingsResponse {
  message: string;
  data: Building[];
  count: number;
  complex: {
    id: number;
    name: string;
    code: number;
  };
}

interface UnitsResponse {
  message: string;
  data: Unit[];
  count: number;
  building: {
    id: number;
    dongNumber: number;
    dongName?: string | null;
    shapeType?: string | null;
    coordinates?: string | null;
    maxFloor: number;
    complex: {
      id: number;
      name: string;
      code: number;
    };
  };
}

const fetchComplexes = async (): Promise<ComplexResponse> => {
  const response = await fetch('/api/complexes');
  if (!response.ok) {
    throw new Error('Failed to fetch complexes');
  }
  return response.json();
};

const fetchBuildings = async (
  complexId: number
): Promise<BuildingsResponse> => {
  const response = await fetch(`/api/buildings?complexId=${complexId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch buildings');
  }
  return response.json();
};

const fetchUnits = async (buildingId: number): Promise<UnitsResponse> => {
  const response = await fetch(`/api/units?buildingId=${buildingId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch units');
  }
  return response.json();
};

const ComplexDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const complexId = parseInt(id || '0');
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(
    null
  );
  const [showBuildingsModal, setShowBuildingsModal] = useState(false);
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [buildingTypeFilter, setBuildingTypeFilter] = useState<string>('all');
  const [accessibilityFilter, setAccessibilityFilter] = useState<string>('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBuildingForEdit, setSelectedBuildingForEdit] =
    useState<Building | null>(null);
  const [showInfoEditModal, setShowInfoEditModal] = useState(false);
  const [selectedBuildingForInfoEdit, setSelectedBuildingForInfoEdit] =
    useState<Building | null>(null);
  const [focusedBuildingId, setFocusedBuildingId] = useState<number | null>(null);

  // Helper function to simplify Unit Code by removing prefix
  const simplifyUnitCode = (code: string): string => {
    // Remove prefix like "658-" from "658-101" to show just "101"
    const dashIndex = code.indexOf('-');
    return dashIndex !== -1 ? code.substring(dashIndex + 1) : code;
  };

  // Helper functions for displaying building classification
  const getBuildingTypeBadge = (buildingType?: string) => {
    switch (buildingType) {
      case 'corridor':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            복도식
          </span>
        );
      case 'staircase':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            계단식
          </span>
        );
      case 'general':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            일반식
          </span>
        );
      case 'officetel':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            오피스텔
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            미분류
          </span>
        );
    }
  };

  const getAccessibilityIcon = (accessibility?: string) => {
    switch (accessibility) {
      case 'accessible':
        return (
          <span className="text-green-500" title="접근 가능">
            ✓
          </span>
        );
      case 'restricted':
        return (
          <span className="text-yellow-500" title="제한적 접근">
            ⚠
          </span>
        );
      case 'inaccessible':
        return (
          <span className="text-red-500" title="출입 불가">
            ✗
          </span>
        );
      default:
        return (
          <span className="text-gray-400" title="미분류">
            ?
          </span>
        );
    }
  };

  const handleFocusBuilding = (buildingId: number) => {
    setFocusedBuildingId(buildingId);
    const mapElement = document.getElementById('complex-map');
    if (mapElement) {
      mapElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Filter buildings based on selected filters
  const getFilteredBuildings = (buildings: Building[]) => {
    return buildings.filter(building => {
      const typeMatch =
        buildingTypeFilter === 'all' ||
        building.buildingType === buildingTypeFilter;
      const accessMatch =
        accessibilityFilter === 'all' ||
        building.accessibility === accessibilityFilter;
      return typeMatch && accessMatch;
    });
  };

  const { data: complexesData, isLoading: complexesLoading } = useQuery({
    queryKey: ['complexes'],
    queryFn: fetchComplexes,
  });

  const { data: buildingsData, isLoading: buildingsLoading } = useQuery({
    queryKey: ['buildings', complexId],
    queryFn: () => fetchBuildings(complexId),
    enabled: !!complexId,
  });

  const { data: unitsData, isLoading: unitsLoading } = useQuery({
    queryKey: ['units', selectedBuildingId],
    queryFn: () => fetchUnits(selectedBuildingId!),
    enabled: !!selectedBuildingId,
  });

  const mapBuildings = useMemo(
    () =>
      (buildingsData?.data ?? []).map(building => ({
        id: building.id,
        dongNumber: building.dongNumber,
        dongName: building.dongName ?? null,
        shapeType: building.shapeType ?? 'polygon',
        coordinates: building.coordinates
      })),
    [buildingsData?.data]
  );

  const unitsBuilding = unitsData?.building;
  const unitsBuildingMapData =
    unitsBuilding && unitsBuilding.coordinates
      ? [
          {
            id: unitsBuilding.id,
            dongNumber: unitsBuilding.dongNumber,
            dongName: unitsBuilding.dongName ?? null,
            shapeType: unitsBuilding.shapeType ?? 'polygon',
            coordinates: unitsBuilding.coordinates
          }
        ]
      : [];
  const unitsBuildingLabel = unitsBuilding
    ? formatDongDisplayName(unitsBuilding.dongNumber, unitsBuilding.dongName)
    : '동 정보 없음';

  useEffect(() => {
    const buildingList = buildingsData?.data ?? [];
    if (buildingList.length === 0) {
      setFocusedBuildingId(null);
      return;
    }
    if (
      focusedBuildingId === null ||
      !buildingList.some(building => building.id === focusedBuildingId)
    ) {
      const firstBuilding = buildingList[0];
      if (firstBuilding) {
        setFocusedBuildingId(firstBuilding.id);
      }
    }
  }, [buildingsData?.data, focusedBuildingId]);

  useEffect(() => {
    if (selectedBuildingId !== null) {
      setFocusedBuildingId(selectedBuildingId);
    }
  }, [selectedBuildingId]);

  const complex = complexesData?.data.find(c => c.id === complexId);

  if (complexesLoading) {
    return (
      <div className="page-wide">
        <div className="card p-4 sm:p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-96 bg-gray-200 rounded"></div>
              <div className="h-96 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!complex) {
    return (
      <div className="page-wide">
        <div className="card card-body text-center">
          <h1 className="page-title mb-2">단지를 찾을 수 없습니다</h1>
          <p className="text-gray-500 text-sm mb-4">ID {id}인 단지를 찾을 수 없습니다.</p>
          <Link to="/complexes" className="text-brand-600 hover:text-brand-700 text-sm">
            ← 단지 목록으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wide">
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="page-title">
              {complex.name}
              {complex.alias && (
                <span className="text-xl text-gray-500 ml-2">
                  ({complex.alias})
                </span>
              )}
            </h1>
            <p className="text-gray-600 mt-1">{complex.address}</p>
            {complex.groupName && (
              <p className="text-sm text-indigo-600 mt-1">
                그룹: {complex.groupName}
              </p>
            )}
          </div>
          <div className="text-right">
            <span className="text-sm text-gray-500">ID: {complex.id}</span>
            <br />
            <span className="text-sm text-gray-500">Code: {complex.code}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div id="complex-map">
            <h2 className="text-xl font-semibold mb-4">카카오 지도</h2>
            <KakaoMap
              height="384px"
              buildings={mapBuildings}
              focusBuildingId={focusedBuildingId}
              onBuildingClick={buildingId => {
                setFocusedBuildingId(buildingId);
                setSelectedBuildingId(buildingId);
                setShowUnitsModal(true);
              }}
            />
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">동 및 세대</h2>
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">동 목록</h3>
                  <span className="text-sm text-gray-500">
                    {buildingsData?.count || complex.buildings.length}개 동
                  </span>
                </div>
                <p className="text-gray-500 text-sm mb-3">
                  이 단지의 모든 동을 보려면 클릭하세요
                </p>
                <button
                  onClick={() => setShowBuildingsModal(true)}
                  className="bg-blue-500 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded"
                >
                  동 보기
                </button>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-2">요약 통계</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">총 동 수:</span>
                    <p className="font-medium">{complex.buildings.length}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">총 세대 수:</span>
                    <p className="font-medium">
                      {complex.buildings.reduce(
                        (sum, b) => sum + (b._count?.units || 0),
                        0
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Buildings Modal */}
      {showBuildingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Buildings in {complex.name}
              </h3>
              <button
                onClick={() => setShowBuildingsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {buildingsLoading ? (
              <div className="text-center py-8">동 정보를 불러오는 중...</div>
            ) : (
              <>
                {/* Filter Controls */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    필터
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        동 유형
                      </label>
                      <select
                        value={buildingTypeFilter}
                        onChange={e => setBuildingTypeFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">전체</option>
                        <option value="corridor">복도식</option>
                        <option value="staircase">계단식</option>
                        <option value="general">일반식</option>
                        <option value="officetel">오피스텔</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        접근성
                      </label>
                      <select
                        value={accessibilityFilter}
                        onChange={e => setAccessibilityFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">전체</option>
                        <option value="accessible">접근 가능</option>
                        <option value="restricted">제한적 접근</option>
                        <option value="inaccessible">출입 불가</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Buildings Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {getFilteredBuildings(buildingsData?.data || []).map(building => {
                    const isFocused = focusedBuildingId === building.id;
                    return (
                      <div
                        key={building.id}
                        className={`rounded-lg p-4 transition-shadow ${
                          isFocused ? 'border-2 border-blue-500 bg-blue-50 shadow-sm' : 'border border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">
                              {formatDongDisplayName(building.dongNumber, building.dongName)}
                            </h4>
                            {getAccessibilityIcon(building.accessibility)}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleFocusBuilding(building.id)}
                              className="text-xs text-blue-600 hover:text-blue-800 underline"
                            >
                              지도보기
                            </button>
                            <span className="text-sm text-gray-500">
                              {building._count.units}세대
                            </span>
                          </div>
                        </div>

                        <div className="mb-3">
                          {getBuildingTypeBadge(building.buildingType)}
                        </div>

                        <div className="text-sm text-gray-600 mb-3 space-y-1">
                          <p>최고층: {building.maxFloor}층</p>
                          <p>형태: {building.shapeType}</p>
                          {building.hasElevator !== null && (
                            <p>
                              엘리베이터: {building.hasElevator ? '있음' : '없음'}
                            </p>
                          )}
                          {building.hasIntercom !== undefined &&
                            building.hasIntercom !== null && (
                              <p>
                                인터폰: {building.hasIntercom ? '있음' : '없음'}
                              </p>
                            )}
                          {building.entranceCount && (
                            <p>입구 수: {building.entranceCount}개</p>
                          )}
                          {building.accessNotes && (
                            <p className="text-xs text-gray-500 italic">
                              {building.accessNotes}
                            </p>
                          )}
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setFocusedBuildingId(building.id);
                              setSelectedBuildingId(building.id);
                              setShowBuildingsModal(false);
                              setShowUnitsModal(true);
                            }}
                            className="flex-1 bg-green-500 hover:bg-green-700 text-white text-sm py-1 px-3 rounded"
                          >
                            세대 보기
                          </button>
                          <button
                            onClick={() => {
                              setFocusedBuildingId(building.id);
                              setSelectedBuildingForInfoEdit({
                                ...building,
                                complex: {
                                  id: complex.id,
                                  name: complex.name,
                                  code: complex.code
                                }
                              });
                              setShowInfoEditModal(true);
                            }}
                            className="flex-1 bg-indigo-500 hover:bg-indigo-700 text-white text-sm py-1 px-3 rounded"
                          >
                            동 정보
                          </button>
                          <button
                            onClick={() => {
                              setFocusedBuildingId(building.id);
                              setSelectedBuildingForEdit({
                                ...building,
                                complex: {
                                  id: complex.id,
                                  name: complex.name,
                                  code: complex.code,
                                },
                              });
                              setShowEditModal(true);
                            }}
                            className="flex-1 bg-blue-500 hover:bg-blue-700 text-white text-sm py-1 px-3 rounded"
                          >
                            분류 편집
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* No results message */}
                {getFilteredBuildings(buildingsData?.data || []).length ===
                  0 && (
                  <div className="text-center py-8 text-gray-500">
                    선택한 필터 조건에 맞는 동이 없습니다.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Units Modal */}
      {showUnitsModal && selectedBuildingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {unitsData ? formatDongDisplayName(unitsData.building.dongNumber, unitsData.building.dongName) : ''} 세대 목록
                <span className="text-sm text-gray-500 ml-2">
                  ({unitsData?.building.complex.name})
                </span>
              </h3>
              <button
                onClick={() => {
                  setShowUnitsModal(false);
                  setSelectedBuildingId(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {unitsLoading ? (
              <div className="text-center py-8">세대 정보를 불러오는 중...</div>
            ) : (
              <div className="space-y-4">
                {unitsBuilding ? (
                  unitsBuildingMapData.length > 0 ? (
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">동 위치</h4>
                      <KakaoMap
                        height="240px"
                        buildings={unitsBuildingMapData}
                        focusBuildingId={unitsBuilding.id}
                      />
                    </div>
                  ) : (
                    <div className="border border-dashed border-gray-300 rounded-lg p-4 text-sm text-gray-500">
                      이 동에는 좌표 정보가 등록되어 있지 않습니다.
                    </div>
                  )
                ) : null}

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          세대 코드
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          층
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          호
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          동
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {unitsData?.data.map(unit => (
                        <tr key={unit.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {simplifyUnitCode(unit.code)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {unit.floor}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {unit.suffix}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {unitsBuildingLabel}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {unitsData?.data.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      이 동에서 세대를 찾을 수 없습니다
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Building Classification Edit Modal */}
      {showEditModal && selectedBuildingForEdit && (
        <BuildingClassificationEditModal
          building={selectedBuildingForEdit}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedBuildingForEdit(null);
          }}
        />
      )}

      {/* Building Info Edit Modal */}
      {showInfoEditModal && selectedBuildingForInfoEdit && (
        <BuildingInfoEditModal
          building={selectedBuildingForInfoEdit}
          isOpen={showInfoEditModal}
          onClose={() => {
            setShowInfoEditModal(false);
            setSelectedBuildingForInfoEdit(null);
          }}
        />
      )}
    </div>
  );
};

export default ComplexDetailPage;
